/**
 * 매뉴얼 콘텐츠 로더.
 *
 * 콘텐츠 정본은 레포의 마크다운(`docs/manual/`)이고, 빌드 타임에 원문을 그대로 번들에 싣는다.
 * (런타임 fetch 없음 — 문서가 곧 코드와 같은 커밋에 묶인다.)
 *
 * dev 서버는 admin 루트 밖 파일 서빙을 기본 거부하므로 `vite.config.ts`의 `server.fs.allow`가
 * 레포 루트를 허용하고 있다.
 */
import {
  buildManualIndex,
  type ManualCover,
  type ManualDoc,
  type ManualIndex,
  type ManualNode,
} from './manualParser';

export {
  MANUAL_SECTIONS,
  MANUAL_SECTION_LABELS,
  type ManualCover,
  type ManualDoc,
  type ManualDocMeta,
  type ManualNode,
  type ManualSectionId,
  type ManualSectionNode,
} from './manualParser';

// `apps/admin/src/pages/manual/` → 레포 루트까지 5단계.
const rawFiles = import.meta.glob('../../../../../docs/manual/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const index: ManualIndex = buildManualIndex(rawFiles);

/** 매뉴얼 목록 (표지 order 순 · 각 매뉴얼 안은 규약 섹션 순 → order → slug). */
export const manuals: ManualNode[] = index.manuals;

/** 정렬이 끝난 전체 문서 (P4 검색 인덱스의 입력 — 각 문서가 `manual`을 갖는다). */
export const manualDocs: ManualDoc[] = index.docs;

const manualById = new Map<string, ManualNode>(index.manuals.map((m) => [m.id, m]));

const byKey = new Map<string, ManualDoc>(
  index.docs.map((doc) => [`${doc.manual}/${doc.section}/${doc.slug}`, doc]),
);

/** 없는 매뉴얼이면 `undefined` — 호출부가 "찾을 수 없습니다"를 렌더한다(throw 금지). */
export function getManual(manual: string): ManualNode | undefined {
  return manualById.get(manual);
}

/** 매뉴얼 표지 — 표지 파일(`index.md`)이 없는 매뉴얼이면 `undefined`. */
export function getCover(manual: string): ManualCover | undefined {
  return manualById.get(manual)?.cover;
}

/** 없는 문서면 `undefined` — 호출부가 "찾을 수 없습니다"를 렌더한다(throw 금지). */
export function getDoc(manual: string, section: string, slug: string): ManualDoc | undefined {
  return byKey.get(`${manual}/${section}/${slug}`);
}

/**
 * slug만으로 문서를 찾는다 (`related` 링크 해석용 — slug는 한 매뉴얼 안에서 유일하다고 본다).
 * 매뉴얼 경계를 넘지 않는다: 다른 매뉴얼의 같은 slug로 새는 것을 막는다.
 */
export function findDocBySlug(manual: string, slug: string): ManualDoc | undefined {
  return manualById.get(manual)?.docs.find((doc) => doc.slug === slug);
}
